import { useState } from 'react';
import { ChevronDown, ChevronRight, Layers } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { useToast } from '@/components/ui/use-toast';
import { cn } from '@/lib/utils';
import { useUnignoreUnits, type DuplicateGroup, type GroupMember } from '@/api/groups';
import { IgnoreConfirmDialog } from './IgnoreConfirmDialog';

const PUBLISHED_STATUS = 'published';

function isIgnored(member: GroupMember): boolean {
  return member.isBlocked && member.blockedReason === 'duplicate';
}

interface DuplicateGroupCardProps {
  group: DuplicateGroup;
  partnerAccountId: number;
}

export function DuplicateGroupCard({
  group,
  partnerAccountId,
}: DuplicateGroupCardProps): JSX.Element {
  const [expanded, setExpanded] = useState(true);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [confirmOpen, setConfirmOpen] = useState(false);

  const { toast } = useToast();
  const unignoreMutation = useUnignoreUnits();

  const { members } = group;
  const headerName = group.parent?.name ?? members[0]?.name ?? 'Unknown group';

  const toggle = (id: string, checked: boolean) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(id);
      } else {
        next.delete(id);
      }
      return next;
    });
  };

  const selectAllButFirst = () => {
    setSelected(new Set(members.slice(1).map((m) => m.id)));
  };

  const selectedMembers = members.filter((m) => selected.has(m.id));
  const hasSelection = selectedMembers.length > 0;

  const handleUnignore = () => {
    const syncedLocationIds = selectedMembers.map((m) => Number(m.id));
    unignoreMutation.mutate(
      { partnerAccountId, groupKey: group.groupKey, syncedLocationIds },
      {
        onSuccess: ({ unignored }) => {
          toast({
            title: 'Units un-ignored',
            description: `Un-ignored ${String(unignored)} unit${unignored === 1 ? '' : 's'}.`,
          });
          setSelected(new Set());
        },
        onError: (err) => {
          const message = err instanceof Error ? err.message : 'Failed to un-ignore units';
          toast({
            title: 'Error',
            description: message,
            variant: 'destructive',
          });
        },
      }
    );
  };

  return (
    <Card data-testid={`group-card-${group.groupKey}`}>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <button
            type="button"
            className="flex items-center gap-2 text-left"
            onClick={() => { setExpanded((e) => !e); }}
            data-testid={`group-toggle-${group.groupKey}`}
          >
            {expanded ? (
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            ) : (
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            )}
            <Layers className="h-4 w-4 text-muted-foreground" />
            <CardTitle className="text-base">{headerName}</CardTitle>
          </button>
          <Badge variant="secondary" className="text-xs">
            {String(group.memberCount)} units
          </Badge>
        </div>
      </CardHeader>

      {expanded && (
        <CardContent>
          <div className="space-y-2">
            {members.map((member) => {
              const ignored = isIgnored(member);
              return (
                <div
                  key={member.id}
                  className="flex items-center gap-3 rounded-lg border bg-muted/30 p-3"
                >
                  <Checkbox
                    checked={selected.has(member.id)}
                    onCheckedChange={(checked) => { toggle(member.id, checked === true); }}
                    data-testid={`group-member-checkbox-${member.id}`}
                  />
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-medium">{member.name}</p>
                    <p className="truncate text-xs text-muted-foreground">{member.externalId}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {ignored && (
                      <Badge
                        variant="outline"
                        className={cn('text-xs', 'border-amber-200 bg-amber-100 text-amber-800')}
                      >
                        ignored
                      </Badge>
                    )}
                    <Badge
                      variant={member.publicationStatus === PUBLISHED_STATUS ? 'default' : 'outline'}
                      className="text-xs"
                    >
                      {member.publicationStatus}
                    </Badge>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={selectAllButFirst}
              data-testid="group-select-all-but-first"
            >
              Select all but first
            </Button>
            <div className="flex-1" />
            <Button
              variant="outline"
              size="sm"
              onClick={handleUnignore}
              disabled={!hasSelection || unignoreMutation.isPending}
              data-testid="group-unignore-btn"
            >
              Un-ignore selected
            </Button>
            <Button
              variant="destructive"
              size="sm"
              onClick={() => { setConfirmOpen(true); }}
              disabled={!hasSelection}
              data-testid="group-ignore-btn"
            >
              Ignore selected
            </Button>
          </div>
        </CardContent>
      )}

      <IgnoreConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        partnerAccountId={partnerAccountId}
        groupKey={group.groupKey}
        members={selectedMembers}
      />
    </Card>
  );
}
